import { useEffect, useRef, useState } from "react";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { mensagemDaAbertura, type MotivoAbertura } from "../lib/abrir";
import {
  consultar,
  ehFalha,
  ehInferido,
  ratEmTexto,
  type Cnae,
  type Resultado,
} from "../lib/cnaexpert";

/**
 * O CNAExpert (000216): "qual o FPAS e o RAT deste CNAE?"
 *
 * ## A tela mostra TRÊS números, e eles são de normas diferentes
 *
 * **FPAS** (o código do fundo previdenciário), **RAT** (a alíquota de Riscos
 * Ambientais do Trabalho, em percentual) e **grau de risco** (o da NR-4, de 1 a
 * 4). Os dois últimos parecem o mesmo número e não são: medido na 000184, grau
 * 3 aparece com RAT 1 em 244 CNAEs, com RAT 2 em 350 e com RAT 3 em 88.
 * Derivar um do outro produziria número errado com cara de certo, e é por isso
 * que a tela mostra os três lado a lado com o nome de cada um.
 *
 * ## A procedência vai junto do número
 *
 * 151 dos 1.333 CNAEs do catálogo têm FPAS/RAT **inferido** — da classe `/00`
 * ou da família —, não lido de tabela. Esses aparecem com o aviso ao lado.
 * Número certo sem procedência ensina a confiar em número velho; número
 * inferido sem aviso é pior, porque quem fecha a folha não tem como saber.
 *
 * ## Os enquadramentos alternativos
 *
 * 92 CNAEs têm mais de um FPAS possível — "Fabricação de álcool" é 833 se
 * agroindústria e 507 se indústria. O catálogo do hub guarda UM (e marca 91
 * deles como "exato com alternativas"); os outros vêm de
 * `cnae_enquadramentos`, a tabela filha que a 000216 criou para isso. A tela
 * mostra a lista inteira e diz qual o catálogo adota — mostrar só o principal
 * daria uma resposta certa para metade dos casos e errada para a outra.
 *
 * ## O que a tela NÃO afirma
 *
 * Não achar um código aqui não quer dizer que ele não exista: o catálogo é o da
 * CNAE vigente no hub, e código extinto (uma subclasse que virou três) não está
 * nele. O rodapé diz isso.
 *
 * ## Sem estado na URL
 *
 * O que se compartilha é a FERRAMENTA, não a consulta — ninguém manda ao colega
 * "a busca do 4120-4/00". É a mesma decisão do SindiFácil e da busca de
 * ocupações do MEI.
 */

/** A espera antes de pedir ao servidor — a mesma do SindiFácil, e pela mesma
 *  razão: quem digita "construção" gasta uma viagem, não dez. */
const ESPERA_MS = 350;

export default function CnaExpert() {
  const [termo, setTermo] = useState("");
  const [res, setRes] = useState<Resultado | null>(null);
  const [buscando, setBuscando] = useState(true);
  const [recusa, setRecusa] = useState<MotivoAbertura | null>(null);
  const [erro, setErro] = useState(false);

  // A chamada em voo. Sem isto, a resposta lenta de uma busca ANTERIOR chega
  // depois da atual e sobrescreve o resultado certo pelo velho — defeito
  // invisível e intermitente, que aparece justamente com internet ruim.
  const rodada = useRef(0);

  useEffect(() => {
    const minha = ++rodada.current;
    setBuscando(true);
    const t = setTimeout(() => {
      void (async () => {
        const r = await consultar(termo);
        if (minha !== rodada.current) return;
        if (r === null) setErro(true);
        else if (ehFalha(r)) setRecusa(r.motivo as MotivoAbertura);
        else {
          setRes(r);
          setRecusa(null);
        }
        setBuscando(false);
      })();
    }, ESPERA_MS);
    return () => clearTimeout(t);
  }, [termo]);

  if (recusa) {
    const m = mensagemDaAbertura(recusa);
    return (
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="rounded-cf-lg border border-borda bg-superficie p-6">
          <h1 className="font-display text-xl font-bold text-titulo">{m.titulo}</h1>
          <p className="mt-2 text-sm leading-relaxed text-cinza">{m.texto}</p>
        </div>
      </main>
    );
  }

  if (erro) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <p className="rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar a tabela de CNAEs agora. Recarregue a página; se
          continuar, fale com o suporte.
        </p>
      </main>
    );
  }

  if (!res && buscando) return <Carregando />;

  const cortou = res !== null && res.total > res.cnaes.length;

  return (
    <main className="mx-auto max-w-5xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Consulta"
        titulo="CNAExpert"
        descricao="Digite o código CNAE ou uma palavra da atividade para ver o FPAS, a alíquota RAT e o grau de risco."
        rodape={
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
              CNAE ou atividade
            </span>
            <input
              className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-4 text-base text-white placeholder:text-navy-mute/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
              type="search"
              id="filtro-termo"
              autoFocus
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              placeholder="4120-4/00, construção de edifícios, cultivo de arroz…"
            />
          </label>
        }
      />

      {res && (
        <>
          <p role="status" data-total={res.total} className="mt-6 text-sm text-cinza">
            {res.total === 0
              ? "Nenhum CNAE encontrado."
              : `${res.total.toLocaleString("pt-BR")} ${res.total === 1 ? "atividade" : "atividades"}` +
                // O total DIZ quantos casaram quando a lista corta. Sem isso,
                // uma lista truncada vira "não existe" na cabeça de quem procura.
                (cortou ? ` · mostrando as primeiras ${res.cnaes.length}` : "")}
          </p>

          {res.total === 0 && (
            <div className="mt-3 rounded-xl bg-superficie-2 p-5 text-sm leading-relaxed text-cinza">
              <p className="font-semibold text-titulo">
                Não achar aqui não quer dizer que o código não exista.
              </p>
              <p className="mt-1">
                A tabela é a da CNAE vigente. Código que foi desdobrado — uma subclasse
                que virou três — não está mais nela. Tente a descrição da atividade, ou
                só os quatro primeiros dígitos para ver a família inteira.
              </p>
            </div>
          )}

          {cortou && (
            <p className="mt-2 text-xs text-cinza">
              Refine a busca para ver o restante: a lista mostra {res.teto} por vez.
            </p>
          )}

          <ul className="mt-3 flex flex-col gap-3">
            {res.cnaes.map((c) => (
              <Ficha key={c.codigo} c={c} />
            ))}
          </ul>
        </>
      )}

      <p className="mt-8 rounded-xl bg-superficie-2 p-4 text-xs leading-relaxed text-cinza">
        FPAS, RAT e grau de risco saem da tabela de CNAEs mantida pela Contabilidade
        Facilitada — a mesma que o Radar Simples e a consulta de ocupações do MEI
        usam. O RAT é a alíquota base: o valor que a empresa recolhe é ele
        multiplicado pelo FAP dela, que é individual e sai do portal do INSS.
        Confira o enquadramento na GFIP/eSocial antes de fechar a folha.
      </p>
    </main>
  );
}

function Ficha({ c }: { c: Cnae }) {
  const rat = ratEmTexto(c.rat);
  const inferido = ehInferido(c.criterio);

  return (
    <li
      data-cnae={c.codigo}
      data-inferido={inferido ? "sim" : "nao"}
      className="surface rounded-cf-lg border border-borda p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span data-codigo className="font-display text-lg font-bold tabular-nums text-titulo">
          {c.codigo}
        </span>
        <p data-descricao className="min-w-0 flex-1 text-sm leading-relaxed text-tinta">
          {c.descricao}
        </p>
      </div>

      {/* Os três números, cada um com o NOME dele. RAT e grau de risco parecem o
          mesmo número e são de normas diferentes — juntá-los seria repetir o
          defeito que a 000184 mediu e nomeou. */}
      <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
        <Fato rotulo="FPAS" valor={c.fpas} atributo="fpas" />
        <Fato rotulo="RAT" valor={rat} atributo="rat" />
        <Fato rotulo="Grau de risco (NR-4)" valor={c.grau_risco?.toString() ?? null} atributo="grau" />
      </dl>

      {c.alternativos.length > 0 && (
        <div data-alternativos className="mt-3 rounded-xl bg-tile p-3">
          <p className="text-xs font-bold tracking-wide text-titulo uppercase">
            Outros enquadramentos de FPAS
          </p>
          <ul className="mt-1.5 flex flex-col gap-1">
            {c.alternativos.map((a) => (
              <li key={a.fpas} data-alternativo={a.fpas} className="text-sm leading-relaxed text-cinza">
                <span className="font-semibold tabular-nums text-tinta">{a.fpas}</span>
                {/* A descrição é o que DISTINGUE a alternativa. Sem ela a tela
                    mostraria "507 / 833" e o aluno não saberia qual é indústria
                    e qual é agroindústria — número sem significado na frente de
                    quem vai enquadrar uma folha. */}
                {a.descricao ? ` — ${a.descricao}` : null}
                {a.fpas === c.fpas && (
                  <span className="ml-1.5 rounded-full bg-chip px-2 py-0.5 text-xs font-bold text-acento">
                    o adotado aqui
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* A procedência. Só aparece quando o número foi INFERIDO: dizer "exato"
          em 1.182 fichas seria ruído, e o que muda a conduta de quem lê é
          justamente o outro caso. É a mesma razão de "Prorrogação — não cabe"
          no HUB DP: o ramo que carrega o aviso precisa de texto próprio. */}
      {inferido && (
        <p data-criterio className="mt-3 rounded-lg bg-alerta-fundo px-3 py-2 text-xs leading-relaxed text-alerta-tinta">
          <strong className="font-bold">FPAS e RAT inferidos</strong> — {c.criterio}. Este
          código não estava na tabela de origem com enquadramento próprio; confirme na
          norma antes de aplicar.
        </p>
      )}

      {c.base_legal && (
        <p className="mt-2 text-xs text-cinza">{c.base_legal}</p>
      )}
    </li>
  );
}

function Fato({
  rotulo,
  valor,
  atributo,
}: {
  rotulo: string;
  valor: string | null;
  atributo: string;
}) {
  return (
    <div>
      <dt className="text-xs font-bold tracking-wide text-cinza uppercase">{rotulo}</dt>
      {/* Nulo é "o catálogo não diz", que é diferente de zero — e a diferença
          importa para quem vai orientar um cliente. Mostrar "0" onde não há
          dado seria afirmar uma alíquota que ninguém apurou. */}
      <dd data-fato={atributo} className="mt-0.5 text-base font-semibold tabular-nums text-titulo">
        {valor ?? <span className="text-sm font-normal text-cinza italic">não informado</span>}
      </dd>
    </div>
  );
}
